/**
 * Markers overlay — renders scene.json `markers[]` as billboarded HTML dots
 * over the canvas, with a hover title and a click-to-expand title+body
 * popover. (HTML overlay rather than in-scene geometry: crisp text at any
 * zoom, trivial hit-testing, no extra render pass.)
 *
 * Each frame we project every marker's world position to screen via the
 * camera component's worldToScreen (same CSS-pixel space the controllers'
 * screenToWorld uses) and move its dot there. Markers behind the camera or
 * off-canvas are hidden. The popover follows its dot while open.
 *
 * Schema (documentation/scene-schema.md):
 *   markers[] = { id, position: [x,y,z], title, body? }   // body markdown-ish
 */

import * as pc from 'playcanvas';

import type { Marker } from '../scene/scene-types';

interface MarkerEntry {
    data: Marker;
    world: pc.Vec3;
    dot: HTMLButtonElement;
}

export interface MarkersOverlayOptions {
    parent: HTMLElement;            // uiRoot — overlays the canvas
    app: pc.AppBase;
    cameraEntity: pc.Entity;
    markers: Marker[];
}

const tmpScreen = new pc.Vec3();
const tmpDir = new pc.Vec3();

export class MarkersOverlay {
    private root: HTMLDivElement;
    private entries: MarkerEntry[] = [];
    private popover: HTMLDivElement;
    private openId: string | null = null;
    private app: pc.AppBase;
    private cameraEntity: pc.Entity;
    private onUpdate: () => void;

    constructor(opts: MarkersOverlayOptions) {
        this.app = opts.app;
        this.cameraEntity = opts.cameraEntity;

        // Container layer (pointer-events: none so it never blocks camera
        // drags; individual dots/popover re-enable pointer-events).
        // The look lives in viewer.css (.ollo-markers, .ollo-marker-*); only
        // what changes per frame (display, left, top) is set here.
        this.root = document.createElement('div');
        this.root.className = 'ollo-markers';
        opts.parent.appendChild(this.root);

        // Shared popover (one element reused for whichever marker is open).
        this.popover = document.createElement('div');
        this.popover.className = 'ollo-marker-popover';
        this.popover.style.display = 'none';
        // Clicks inside the popover shouldn't close it.
        this.popover.addEventListener('pointerdown', e => e.stopPropagation());
        this.root.appendChild(this.popover);

        for (const data of opts.markers) {
            const world = new pc.Vec3(data.position[0], data.position[1], data.position[2]);
            const dot = document.createElement('button');
            dot.className = 'ollo-marker-dot';
            dot.type = 'button';
            dot.title = data.title || '';
            dot.style.display = 'none';
            dot.addEventListener('pointerdown', (e) => {
                e.stopPropagation();   // don't start a camera drag / click-to-move
            });
            dot.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggle(data.id);
            });
            this.root.appendChild(dot);
            this.entries.push({ data, world, dot });
        }

        // Clicking empty space closes the popover.
        this.onDocPointerDown = this.onDocPointerDown.bind(this);
        window.addEventListener('pointerdown', this.onDocPointerDown);

        // Per-frame reposition.
        this.onUpdate = () => this.reposition();
        this.app.on('update', this.onUpdate);
        // Position once immediately so dots don't flash at 0,0.
        this.reposition();
    }

    private onDocPointerDown(): void {
        if (this.openId !== null) this.close();
    }

    private toggle(id: string): void {
        if (this.openId === id) this.close();
        else this.open(id);
    }

    private open(id: string): void {
        const entry = this.entries.find(e => e.data.id === id);
        if (!entry) return;
        this.openId = id;
        const title = entry.data.title || '';
        const body = entry.data.body || '';
        this.popover.innerHTML = '';
        if (title) {
            const t = document.createElement('div');
            t.className = 'ollo-marker-title';
            t.textContent = title;
            this.popover.appendChild(t);
        }
        if (body) {
            const b = document.createElement('div');
            b.className = 'ollo-marker-body';
            b.textContent = body;          // plain text — markdown rendering can come later
            this.popover.appendChild(b);
        }
        this.popover.style.display = 'block';
        this.reposition();
    }

    private close(): void {
        this.openId = null;
        this.popover.style.display = 'none';
    }

    /** Project every marker to screen and move its dot; follow with popover. */
    private reposition(): void {
        const cam = this.cameraEntity.camera;
        if (!cam) return;
        const camPos = this.cameraEntity.getPosition();
        const fwd = this.cameraEntity.forward;
        const rect = this.root.getBoundingClientRect();
        const w = rect.width, h = rect.height;

        for (const entry of this.entries) {
            // Behind-camera cull: vector cam→marker vs camera forward.
            tmpDir.sub2(entry.world, camPos);
            const behind = tmpDir.dot(fwd) <= 0;
            cam.worldToScreen(entry.world, tmpScreen);
            const x = tmpScreen.x, y = tmpScreen.y;
            const onScreen = !behind && x >= -40 && x <= w + 40 && y >= -40 && y <= h + 40;
            if (!onScreen) {
                entry.dot.style.display = 'none';
                if (this.openId === entry.data.id) this.popover.style.display = 'none';
                continue;
            }
            entry.dot.style.display = 'block';
            entry.dot.style.left = `${x}px`;
            entry.dot.style.top = `${y}px`;
            if (this.openId === entry.data.id) {
                this.popover.style.display = 'block';
                this.popover.style.left = `${x}px`;
                this.popover.style.top = `${y}px`;
            }
        }
    }

    dispose(): void {
        this.app.off('update', this.onUpdate);
        window.removeEventListener('pointerdown', this.onDocPointerDown);
        this.root.remove();
    }
}
